import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { rerunOutcomeMessage } from '@/components/encompass/prospects/appraisal/feeAuditChain';
import { rerunAppraisalFeeAudit } from '@/services/appraisalWorkspaceService';
import {
  getAppraisalFeeComplianceReport,
  type AppraisalFeeFinding, type AppraisalFeeComplianceReport
} from '@/services/appraisalFeeComplianceService';

export interface TrackedFeeAudit {
  initial: AppraisalFeeFinding;
  row: AppraisalFeeFinding;
  startedAt: number;
  phase: 'queued' | 'running' | 'settled' | 'rejected';
  message?: string;
}

/** Fee controller shared by the report's table and history. No second table. */
export function useAppraisalFeeCompliance() {
  const [report, setReport] = useState<AppraisalFeeComplianceReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const reportRoot = useRef<HTMLDivElement | null>(null);
  const loadGeneration = useRef(0);
  const mounted = useRef(false);
  const inFlight = useRef(new Set<string>());
  const [submitting, setSubmitting] = useState<Array<{ loanId: string; findingId: string }>>([]);
  const [historyFor, setHistoryFor] = useState<AppraisalFeeFinding | null>(null);
  const [tracking, setTracking] = useState<TrackedFeeAudit | null>(null);
  const watched = useRef(new Map<string, string>());
  const refreshReport = useCallback(async () => {
    const generation = ++loadGeneration.current;
    try {
      const result = await getAppraisalFeeComplianceReport();
      if (mounted.current && generation === loadGeneration.current) {
        setReport(result);
        setError(null);
        const trackedLoans = new Set(watched.current.keys());
        setTracking(previous => {
          if (!previous || previous.phase === 'settled' || previous.phase === 'rejected') {
            return previous;
          }
          if (previous.phase === 'queued' && !trackedLoans.has(previous.initial.loanId || '')) {
            return previous;
          }
          const loanRows = result.rows.filter(row => row.loanId === previous.initial.loanId);
          const row = loanRows.find(item => item.attachmentId === previous.initial.attachmentId || item.id === previous.initial.id);
          const running = loanRows.some(item => item.pending || item.retrying);
          return {
            ...previous, row: row || previous.initial, phase: running ? 'running' : 'settled',
            message: running ? undefined : rerunOutcomeMessage(previous.initial.loanNumber, loanRows)
          };
        });
        for (const [loanId, loanNumber] of [...watched.current]) {
          const rows = result.rows.filter(row => row.loanId === loanId);
          if (rows.some(row => row.pending || row.retrying)) {
            continue;
          }
          watched.current.delete(loanId);
          const message = rerunOutcomeMessage(loanNumber, rows);
          setAnnouncement(message);
          if (rows.some(row => row.needsAttention)) {
            toast.warning(message, { position: 'top-right' });
          } else {
            toast.success(message, { position: 'top-right' });
          }
        }
      }
    } catch (cause) {
      if (mounted.current && generation === loadGeneration.current) {
        const message = cause instanceof Error ? cause.message : 'Fee compliance findings could not be loaded.';
        // A refresh failure must not insert a banner into an already loaded report.
        setAnnouncement(message);
        setError(message);
      }
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    void refreshReport();
    return () => {
      mounted.current = false;
    };
  }, [refreshReport]);
  const pending = report?.rows.some(row => row.pending || row.retrying) || submitting.length > 0;
  useEffect(() => {
    if (!pending) {
      return;
    }
    const poll = () => {
      if (document.visibilityState === 'visible') {
        void refreshReport();
      }
    };
    const interval = window.setInterval(poll, 10000);
    document.addEventListener('visibilitychange', poll);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [pending, refreshReport]);
  const rerunInProgress = (row: AppraisalFeeFinding) => submitting.some(item => item.loanId === (row.loanId || '')) || (
    Boolean(report?.rows.some(finding => finding.loanId === row.loanId && finding.pending)) &&
    !report?.rows.some(finding => finding.loanId === row.loanId && finding.needsAttention)
  );
  const rerun = async (row: AppraisalFeeFinding) => {
    const loanId = row.loanId || '';
    if (inFlight.current.has(loanId) || rerunInProgress(row)) {
      return;
    }
    setTracking({ initial: row, row, startedAt: Date.now(), phase: 'queued' });
    inFlight.current.add(loanId);
    setSubmitting(previous => [...previous, { loanId, findingId: row.id }]);
    setAnnouncement('Queuing invoice audit…');
    try {
      await rerunAppraisalFeeAudit({ loanId, encompassLoanId: row.encompassLoanId || '' });
      if (mounted.current) {
        // Fence an older poll and keep polling even if the first refresh fails.
        loadGeneration.current++;
        setReport(previous => previous && ({
          ...previous, rows: previous.rows.map(finding =>
            finding.loanId === loanId ? { ...finding, pending: true, freshState: 'pending' } : finding)
        }));
        watched.current.set(loanId, row.loanNumber);
        const message = `Audit queued for ${row.loanNumber}. All current appraisal invoices will be read again, then checked against live Encompass fees.`;
        setAnnouncement(message);
        // Keep notifications clear of the docked tracker's actions.
        toast.success(message, { position: 'top-right' });
        await refreshReport();
      }
    } catch (cause) {
      if (mounted.current) {
        const message = cause instanceof Error ? cause.message : 'The audit could not be queued.';
        setTracking(previous => previous?.initial.loanId === loanId ? { ...previous, phase: 'rejected', message } : previous);
        setAnnouncement(message);
        toast.error(message, { position: 'top-right' });
      }
    } finally {
      inFlight.current.delete(loanId);
      if (mounted.current) {
        setSubmitting(previous => previous.filter(item => item.loanId !== loanId));
      }
    }
  };

  return {
    report, error, announcement, reportRoot,
    submitting, tracking, dismissTracker: () => setTracking(null), historyFor, setHistoryFor, refreshReport, rerunInProgress, rerun
  };
}
